import React, { FC, ReactNode } from "react";

export interface LegalCheckProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  className?: string;
}

/**
 * The small print with a tick box in front of it — the confirmation that
 * precedes a signature.
 *
 * The reference ships these pre-ticked. We start them empty: each one is a
 * statement the tenant makes about their own documents, and pre-ticking asserts
 * it on their behalf.
 */
const LegalCheck: FC<LegalCheckProps> = ({
  id,
  checked,
  onChange,
  children,
  className = "",
}) => (
  <label
    htmlFor={id}
    className={`flex cursor-pointer items-start gap-[9px] text-[14px] min-[760px]:text-[13px] leading-[1.5] text-[#777] ${className}`}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="peer sr-only"
    />

    <span
      className={`mt-[2px] flex h-[15px] w-[15px] flex-none items-center justify-center rounded-[2px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-gold peer-focus-visible:ring-offset-1 ${
        checked ? "border-gold bg-gold text-white" : "border-neutral-300 bg-white"
      }`}
    >
      {checked && (
        <svg
          className="h-2 w-2"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12.5l4.5 4.5L19 7" />
        </svg>
      )}
    </span>

    {/* Sized to be read, not skimmed past: this is the statement the tenant
        makes immediately before signing a lease, so it was the one piece of
        copy on the screen that should not have been the smallest. */}
    <span className="font-medium text-[14px] min-[760px]:text-[13px]">
      {children}
    </span>
  </label>
);

export default LegalCheck;
